import { VEHICLES } from '../data/vehicles';
import { getDriverSpecialties } from './driverSpecialties';
import { vehicleSatisfiesContract, driverSatisfiesContract } from './contractCompatibility';
import { estimateContractDailyToll } from './contractToll';

/**
 * Compute the gross margin of a contract, given a vehicle + driver pairing.
 * "Marge brute" = revenu brut − charges variables affectées à la mission
 * (carburant, maintenance proratisée, salaire chauffeur proratisé, assurance
 * proratisée). We do NOT include fixed costs (rent, support payroll, etc.) so
 * the figure stays a true gross margin per the user's request.
 *
 * @returns { revenue, charges, margin, marginPct, details }
 *          where details = breakdown of each charge category.
 */
export function computeContractMargin(contract, vehicle, driver, opts = {}) {
  if (!contract) return null;

  // Determine duration in working days. Régulier contracts use a 30-day total
  // but typically only 22 are productive; punctual contracts use their literal
  // duration.
  const totalDays = contract.frequency === 'ponctuel'
    ? (contract.duration || 1)
    : Math.round((contract.duration || 30) * (22 / 30));
  const dailyRevenue = contract.dailyRevenue || contract.revenue || 0;

  let revenue = dailyRevenue * totalDays;

  // Specialty bonus: if the driver's specialty matches the contract's category
  // we apply the +15 % bonus to the revenue projection.
  if (driver && contract.category) {
    const SPECIALTY_ALIASES = {
      'Messagerie / Colis': ['Messagerie', 'Messagerie / Colis', 'Messagerie express', 'Colis'],
      'BTP / Matériaux': ['BTP', 'BTP / Matériaux', 'BTP lourd', 'Chantier', 'Engins de chantier'],
      'Spécialisé': ['ADR', 'Spécialisé', 'Matières dangereuses', 'Citerne'],
      'Export EU': ['Export EU', 'Longue distance', 'EU', 'International'],
      'Frigorifique': ['Frigorifique', 'GMS', 'Pharma'],
      'Vrac / Palettes': ['Vrac', 'Vrac / Palettes', 'Vrac lourd', 'Palettes', 'Distribution', 'National'],
    };
    const aliases = SPECIALTY_ALIASES[contract.category] || [contract.category];
    const specs = getDriverSpecialties(driver);
    if (specs.some(s => aliases.includes(s))) {
      revenue = revenue * 1.15;
    }
  }

  // If no vehicle yet, return an estimate with placeholder charges = 0.
  if (!vehicle) {
    return {
      revenue: Math.round(revenue),
      charges: 0,
      margin: Math.round(revenue),
      marginPct: 100,
      estimated: true,
      details: {},
    };
  }

  // Resolve the vehicle source (state vehicle may carry the dataId).
  const baseVehicle = VEHICLES.find(v => v.id === vehicle.dataId) || vehicle;
  const dailyFuel = vehicle.dailyFuel ?? baseVehicle.dailyFuel ?? 0;
  const maintenancePerMonth = vehicle.maintenancePerMonth ?? baseVehicle.maintenancePerMonth ?? 0;
  const insurancePerMonth = vehicle.insurancePerMonth ?? baseVehicle.insurancePerMonth ?? 0;
  const driverCost = (driver?.employerCost) ?? 0;

  // Apply the insurance tier multiplier (basic 0.70 / premium 1.35).
  const tier = vehicle.insuranceCoverage || 'standard';
  const insMult = tier === 'basic' ? 0.70 : tier === 'premium' ? 1.35 : 1;

  const charges = {
    fuel: Math.round(dailyFuel * totalDays),
    maintenance: Math.round((maintenancePerMonth / 22) * totalDays),
    driver: Math.round((driverCost / 22) * totalDays),
    insurance: Math.round((insurancePerMonth * insMult / 22) * totalDays),
    // 12/02/2026 — Retour user prod : les péages n'étaient pas
    // comptabilisés dans la marge, alors qu'ils sont réellement
    // débités au quotidien (cf. computeDailyExpenses.dailyToll).
    toll: Math.round(estimateContractDailyToll(contract, vehicle) * totalDays),
  };
  const totalCharges = Object.values(charges).reduce((s, v) => s + v, 0);
  const margin = Math.round(revenue - totalCharges);
  const marginPct = revenue > 0 ? Math.round((margin / revenue) * 100) : 0;

  return {
    revenue: Math.round(revenue),
    charges: totalCharges,
    margin,
    marginPct,
    estimated: false,
    details: charges,
    durationDays: totalDays,
  };
}

/**
 * Cheap optimistic estimate using the most fuel-efficient COMPATIBLE vehicle
 * and a COMPATIBLE driver. Used on the contracts list (Dashboard) where the
 * player hasn't picked a vehicle yet.
 *
 * 13/06/2026 — Cohérence (retour utilisateur : « certaines estimations de
 * marge me semblent incohérentes ») : l'estimation prenait n'importe quel
 * véhicule de tier suffisant (ex. un porteur sec pour un contrat frigo) et
 * n'importe quel chauffeur (le bonus +15 % apparaissait/disparaissait selon
 * la 1ʳᵉ personne libre). Désormais on filtre via les MÊMES règles de
 * compatibilité que la modale d'assignation. Sans paire compatible → null
 * (la table affiche « — » plutôt qu'un chiffre fantaisiste).
 */
export function estimateContractMargin(contract, vehicles, drivers) {
  if (!contract) return null;
  const compatibles = (vehicles || []).filter(
    v => v.status !== 'sold' && vehicleSatisfiesContract(v, contract).ok
  );
  if (compatibles.length === 0) return null;
  // Prefer free vehicles, then the cheapest fuel + tier closest to tierMin.
  compatibles.sort((a, b) => {
    const freeA = (a.status === 'idle' || a.status == null) && !a.assignedContractId ? 0 : 1;
    const freeB = (b.status === 'idle' || b.status == null) && !b.assignedContractId ? 0 : 1;
    if (freeA !== freeB) return freeA - freeB;
    const tierDiff = (a.tier - (contract.tierMin || 1)) - (b.tier - (contract.tierMin || 1));
    if (tierDiff !== 0) return tierDiff;
    return (a.dailyFuel || 0) - (b.dailyFuel || 0);
  });
  const bestVehicle = compatibles[0];
  const pool = drivers || [];
  const bestDriver =
    pool.find(d => !d.assignedVehicleId && driverSatisfiesContract(d, contract).ok)
    || pool.find(d => driverSatisfiesContract(d, contract).ok)
    || null;
  if (!bestDriver) return null;
  return computeContractMargin(contract, bestVehicle, bestDriver);
}
